import { Badge } from "@dl/web-core-js/ui";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { PurchaseOrderOrigin, PurchaseOrderStatus, ReceiptProgress } from "../../api/purchasing";

/** The words for a status, shared by the badge and the "order is now …" toast. */
export function useStatusLabel(): (status: PurchaseOrderStatus) => string {
  const { t } = useTranslation();
  return useCallback(
    (status: PurchaseOrderStatus) =>
      ({
        DRAFT: t("po.status.draft", "Draft"),
        AWAITING_APPROVAL: t("po.status.awaiting-approval", "Awaiting approval"),
        ISSUED: t("po.status.issued", "Issued"),
        CLOSED: t("po.status.closed", "Closed"),
        CANCELLED: t("po.status.cancelled", "Cancelled"),
      })[status],
    [t],
  );
}

/**
 * An order's status as people read it: grey while being written, amber while
 * it waits for the approver, blue when out, green when done.
 */
export function StatusBadge({ status }: { status: PurchaseOrderStatus }) {
  const label = useStatusLabel();
  const variant = { DRAFT: "neutral", AWAITING_APPROVAL: "warning", ISSUED: "info", CLOSED: "success", CANCELLED: "error" } as const;
  return (
    <Badge variant={variant[status]} size="sm">
      {label(status)}
    </Badge>
  );
}

/** The words for a receipt progress, shared by the badge and the list's filter. */
export function useProgressLabel(): (progress: ReceiptProgress) => string {
  const { t } = useTranslation();
  return useCallback(
    (progress: ReceiptProgress) =>
      ({
        NONE: t("po.progress.none", "Nothing received"),
        PARTIAL: t("po.progress.partial", "Partly received"),
        COMPLETE: t("po.progress.complete", "Received"),
      })[progress],
    [t],
  );
}

/** How much has arrived — a separate question from the status, and computed. */
export function ProgressBadge({ progress }: { progress: ReceiptProgress | null | undefined }) {
  const label = useProgressLabel();
  if (!progress) return null;
  const variant = { NONE: "neutral", PARTIAL: "warning", COMPLETE: "success" } as const;
  return (
    <Badge variant={variant[progress]} size="sm">
      {label(progress)}
    </Badge>
  );
}

/**
 * Where an order came from, when that is worth saying. A person's own order
 * says nothing; one drafted from the Replenishment screen, or prepared by the
 * nightly run and not yet touched, says so — the second deserves a check
 * before it is issued.
 */
export function OriginBadge({ origin }: { origin: PurchaseOrderOrigin | null | undefined }) {
  const { t } = useTranslation();
  if (origin === "REPLENISHMENT") {
    return (
      <Badge variant="neutral" size="sm">
        {t("po.origin.replenishment", "From replenishment")}
      </Badge>
    );
  }
  if (origin === "AUTOMATIC") {
    return (
      <Badge variant="info" size="sm">
        {t("po.origin.automatic", "Automatic draft")}
      </Badge>
    );
  }
  return null;
}

/** Issued, but the supplier waits to ship — usually for a full truck. */
export function HoldBadge({ onHold }: { onHold: boolean | null | undefined }) {
  const { t } = useTranslation();
  if (!onHold) return null;
  return (
    <Badge variant="warning" size="sm">
      {t("po.on-hold", "On hold")}
    </Badge>
  );
}
